import * as React from "react";
import { useSelector } from 'react-redux';
import { Dialog } from "@progress/kendo-react-dialogs";
import { DatePicker, DateTimePicker } from "@progress/kendo-react-dateinputs";
import { Field, FieldWrapper, Form, FormElement } from "@progress/kendo-react-form";
import { Input, Switch, TextArea } from "@progress/kendo-react-inputs";
import { Label, Error } from "@progress/kendo-react-labels";
import { DropDownList, MultiSelect } from "@progress/kendo-react-dropdowns";
import { getter } from "@progress/kendo-react-common";
import { NumericTextBox } from "@progress/kendo-react-inputs";
import { FREQUENCIES, buildRecurrenceRule, parseRecurrenceRule } from "../utils/recurrence";

const contentTypeGetter = getter("objectType");
const titleGetter = getter("title");
const emailGetter = getter("email");
const pinnedGetter = getter("pinned");
const dateGetter = getter("date");
const endDateGetter = getter("endDate");
const allDayGetter = getter("allDay");

const startOfDay = (value) => {
  const d = new Date(value);
  d.setHours(0, 0, 0, 0);
  return d;
};

const endOfDay = (value) => {
  const d = new Date(value);
  d.setHours(23, 59, 59, 999);
  return d;
};

// Recurrence needs backend columns that may not be deployed yet; flip off to hide the UI.
export const ENABLE_RECURRENCE = true;

const EMAILREGEX = new RegExp(/(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/i);

const FormInput = (fieldRenderProps) => {
  const { validationMessage, visited, ...others } = fieldRenderProps;
  return (
    <div>
      <Input {...others} />
      {visited && validationMessage && <Error>{validationMessage}</Error>}
    </div>
  );
};

const FormDatePicker = (fieldRenderProps) => {
  return <DatePicker defaultValue={new Date()} {...fieldRenderProps} />
}

// All-day events only carry a date; timed events need the time portion too.
const FormDateTimePicker = (fieldRenderProps) => {
  const { validationMessage, visited, allDay, ...others } = fieldRenderProps;
  const Picker = allDay ? DatePicker : DateTimePicker;
  return (
    <div>
      <Picker width={'100%'} {...others} />
      {visited && validationMessage && <Error>{validationMessage}</Error>}
    </div>
  );
}

const FormRecurrence = (fieldRenderProps) => {
  const { value, onChange, validationMessage, visited } = fieldRenderProps;
  const parsed = parseRecurrenceRule(value);

  const emit = (next) => onChange({ value: buildRecurrenceRule({ ...parsed, ...next }) });

  return (
    <div>
      <DropDownList
        data={FREQUENCIES}
        value={parsed.frequency}
        onChange={(e) => emit({ frequency: e.value })} />
      {parsed.frequency !== FREQUENCIES[0] &&
        <div style={styles.recurrenceRow}>
          <span>Every</span>
          <NumericTextBox
            min={1}
            width={80}
            value={parsed.interval}
            onChange={(e) => emit({ interval: e.value || 1 })} />
          <span>Until</span>
          <DatePicker
            value={parsed.until}
            onChange={(e) => emit({ until: e.value })} />
        </div>}
      {visited && validationMessage && <Error>{validationMessage}</Error>}
    </div>
  );
}

const FormSelect = (fieldRenderProps, setSelectState) => {
  const { validationMessage, visited, ...others } = fieldRenderProps;
  return (
    <div>
      <DropDownList
        required={true} data={["Post", "News", "Event"]} {...others} />
      {visited && validationMessage && <Error>{validationMessage}</Error>}
    </div>
  )
}

const FormSwitch = (fieldRenderProps, label, defaultCheck) => {
  const { validationMessage, visited, ...others } = fieldRenderProps;
  return (
    <div>
      <Switch defaultChecked={defaultCheck} {...others} />
      <label
        style={{ paddingLeft: '10px' }}
        className="k-checkbox-label">
        {label}
      </label>
      {visited && validationMessage && <Error>{validationMessage}</Error>}
    </div>
  );
}

const FormTextArea = (fieldRenderProps) => {
  const {
    validationMessage,
    visited,
    label,
    id,
    valid,
    disabled,
    optional,
    ...others
  } = fieldRenderProps;
  const showValidationMessage = visited && validationMessage;
  return (
    <FieldWrapper>
      <Label editorId={id} editorValid={valid} optional={optional}>
        {label}
      </Label>
      <TextArea
        valid={valid}
        id={id}
        disabled={disabled}
        {...others}
        rows={3}
        placeholder={label}
        style={styles.textArea}
      />
      {showValidationMessage && <Error>{validationMessage}</Error>}
    </FieldWrapper>
  );
};

const ContentForm = (props) => {
  const [selectState, setSelectState] = React.useState(props.initialType || false);

  const formValidator = (values) => {

    var contentTypeVerified = contentTypeGetter(values);
    var titleVerified = titleGetter(values) ? titleGetter(values).trim() : false;
    var emailVerified = emailGetter(values) ? emailGetter(values).trim() && EMAILREGEX.test(emailGetter(values).trim()) : false

    var isEvent = contentTypeVerified === "Event";
    var isAllDay = !!allDayGetter(values);
    var start = dateGetter(values);
    var end = endDateGetter(values);
    // All-day pickers report midnight, so only the calendar day is meaningful.
    var rangeVerified = !isEvent || !end || !start || (isAllDay
      ? startOfDay(end) >= startOfDay(start)
      : new Date(end) >= new Date(start));

    if (contentTypeVerified && titleVerified && rangeVerified && (!emailGetter(values) || emailVerified)) {
      return;
    }
  
    return {
      VALIDATION_SUMMARY: "",
      ...!titleVerified && { ["title"]: "Title cannot be empty." },
      ...!contentTypeVerified && { ["objectType"]: "A type must be selected." },
      ...!rangeVerified && { ["endDate"]: "End must be on or after the start." },
      ...!emailVerified && { ["email"]: "If an email is included, it must be a valid email." }
    };
  };

  const FormMultiSelect = (fieldRenderProps) => {
    const { validationMessage, visited, ...others } = fieldRenderProps;

    return (
      <div>
        <MultiSelect
          required={true}
          data={props.groups}
          textField="name"
          dataItemKey="id"
          {...others} />
        {visited && validationMessage && <Error>{validationMessage}</Error>}
      </div>
    )
  }

  const pinnedSwitch = (fieldRenderProps) => {
    return FormSwitch(fieldRenderProps, "Pinned", props.values.pinned)
  }

  const allDaySwitch = (fieldRenderProps) => {
    return FormSwitch(fieldRenderProps, "All day", props.values.allDay)
  }

  // Store all-day events as a full-day span so downstream consumers see a real duration.
  const handleSubmit = (values, event) => {
    if (values.objectType === "Event" && values.allDay && values.date) {
      return props.onSubmit({
        ...values,
        date: startOfDay(values.date),
        endDate: endOfDay(values.endDate || values.date),
      }, event);
    }
    return props.onSubmit(values, event);
  }

  return <Dialog title={props.isEdit ? 'Edit Content' : 'Create Content'} onClose={props.cancelEdit} style={{ zIndex: 2 }} className='flo-kendo-dialog'>
    <Form
      onSubmit={handleSubmit}
      validator={formValidator}
      initialValues={{
        objectType: props.initialType,
        ...props.values,
        date: props.values.date ? new Date(props.values.date) : new Date(),
        endDate: props.values.endDate ? new Date(props.values.endDate) : null
      }}
      render={(formRenderProps) => (
        <FormElement style={styles.formElement}>
          <fieldset className={"k-form-fieldset"}>
            <div className="mb-3" style={styles.formField}>
              <Field
                id={"contentType"}
                name={"objectType"}
                component={FormSelect}
                onChange={(e) => { setSelectState(e.value) }}
                label={"Type"} />
            </div>
            {( props.isEdit || selectState ) &&
              <>
                <div className="mb-3" style={styles.formField}>
                  <Field name={"title"} component={FormInput} label={"Title"} />
                </div>
                <div className="mb-3" style={styles.formField}>
                  <Field name={"website"} component={FormInput} label={"Link"} />
                </div>
                <div className="mb-3" style={styles.formField}>
                  <Field name={"email"} component={FormInput} label={"Email"} />
                </div>
                <div className="mb-3" style={styles.formField}>
                  <Field name={"details"} component={FormTextArea} label={"Details"} />
                </div>
                {( props.values.objectType === "Event" || selectState === "Event") &&
                  <>
                    <div className="mb-3" style={styles.formField}>
                      <Field name={"address"} component={FormInput} label={"Address"} />
                    </div>
                    <div className="mb-3" style={styles.formField}>
                      <Field name={"location"} component={FormInput} label={"Location"} />
                    </div>
                  </>}
                <div className="mb-3" style={styles.formField}>
                  <Field name={"groups"} component={FormMultiSelect} label={"Groups"} />
                </div>
                {( props.values.objectType === "Event" || selectState === "Event") ?
                  <>
                    <div className="mb-3" style={styles.formField}>
                      <Field name={"allDay"} component={allDaySwitch} label={"All day?"} />
                    </div>
                    <div className="mb-3" style={styles.formField}>
                      <Field
                        name={"date"}
                        component={FormDateTimePicker}
                        allDay={formRenderProps.valueGetter("allDay")}
                        label={"Starts"} />
                    </div>
                    <div className="mb-3" style={styles.formField}>
                      <Field
                        name={"endDate"}
                        component={FormDateTimePicker}
                        allDay={formRenderProps.valueGetter("allDay")}
                        label={"Ends"} />
                    </div>
                    {ENABLE_RECURRENCE &&
                      <div className="mb-3" style={styles.formField}>
                        <Field name={"recurrenceRule"} component={FormRecurrence} label={"Repeats"} />
                      </div>}
                  </> :
                  <div className="mb-3" style={styles.formField}>
                    <Field name={"date"} component={FormDatePicker} label={"Date"} />
                  </div>}
                <div className="mb-3" style={styles.formField}>
                  <Field name={"pinned"} component={pinnedSwitch} label={"Pinned?"} />
                </div>
              </>}
          </fieldset>
          <div className="k-form-buttons k-justify-content-end">
            <button type={"submit"} className="k-button k-primary" style={styles.modalBtn} disabled={!formRenderProps.allowSubmit}>
              {'Save'}
            </button>
            <button type={"submit"} className="k-button" style={styles.modalBtn} onClick={props.cancelEdit}>
              Cancel
            </button>
          </div>
        </FormElement>
      )}
    />
  </Dialog>;
};

const styles = {
  formElement: {
    width: 600,
  },
  formField: {
    marginTop: 10,
    marginBottom: 25,
  },
  textArea: {
    backgroundColor: 'transparent',
  },
  recurrenceRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    marginTop: 10,
  },
  modalBtn: {
    padding: '10px 15px'
  },
}

export default ContentForm;